import { IconCollection } from "components/Icon/IconCollection";
import { getNewRecordPath } from "components/Layout/Sidebar/components/CreateNew/CreateNew";
import { ProvisioningStateDot } from "components/ProvisioningStateDot/ProvisioningStateDot";
import dotStyle from "components/ProvisioningStateDot/ProvisioningStateDot.module.scss";
import RecordLabel from "components/RecordLabel";
import { ShowAllButton } from "components/ShowAllButton/ShowAllButton";
import badgeStyle from "containers/Resources/components/Badges/Badges.module.scss";
import { InTrashBadge } from "containers/Resources/components/Badges/Badges";
import { StateBadges } from "containers/Resources/components/Badges/StateBadges";
import TableTrashToggle from "components/Table/TableTrashToggle";
import { getRecordColumnCellStyle } from "components/Table/recordColumnWidths";
import { locationColumn } from "components/Table/recordTableUtils";
import { commonColumns } from "containers/AdvancedSearch/components/advancedFilterColumns";
import { buildRecordListAdvancedSearchPath } from "containers/Dashboard/advancedSearchLink";
import { getInitialQueryWithTableSettings } from "components/Table/tableUtils";
import { getSchemaFormColumns } from "containers/Resources/screens/ResourcesSingle/AttributeValueSchema";
import {
    RecordDescriptionInlineEdit,
    RecordFieldInlineEditPencil,
    isRecordFieldEditable,
} from "containers/Resources/screens/ResourcesSingle/RecordFieldInlineEdit";
import { SubrecordTable } from "containers/Resources/screens/ResourcesSingle/components/SubrecordTable";
import {
    customObjectListKey,
    initialQuery,
} from "containers/Resources/screens/ResourcesSingle/ResourcesSingle";
import { Custom_Object, Object_Template } from "graphql/generated/graphql";
import { History } from "history";
import { useCallback, useMemo } from "react";
import { getRelativeUrl } from "utils/service";

export const CustomObjectList = ({
    resourceData,
    recordId,
    objectTemplateList,
    fetchResourceData,
    updateCustomObjectListQuery,
    showTrash,
    history,
}: {
    resourceData: Object_Template;
    recordId: string;
    objectTemplateList: Object_Template[];
    fetchResourceData: () => Promise<any>;
    updateCustomObjectListQuery: (query: any) => void;
    /** Whether trashed custom objects are currently shown (allow_deleted). */
    showTrash: boolean;
    history: History;
}) => {
    const customObjectData = useMemo(
        () =>
            resourceData?.custom_object_list_object_template_id?.custom_object_list?.map(
                (customObject) => ({
                    ...customObject,
                    resource_type: "custom_object",
                })
            ),
        [resourceData]
    );

    const columns = useMemo(() => {
        const currentSchema =
            objectTemplateList.find((objectTemplate) => objectTemplate.id === recordId)?.schema ??
            {};

        return [
            {
                label: "Name",
                key: "name",
                shown: true,
                sortable: true,
                cellStyle: getRecordColumnCellStyle("name"),
                dto: (row, refresh) => {
                    const { name, id, resource_type, comment_count, deleted_at } = row;
                    // Match the shared record-list name-cell structure
                    // (commonColumns.name): the provisioning-state dot is absolutely
                    // positioned inside `relativePositionedContainer`, which wraps
                    // ONLY the single-line name/pencil so the dot always centers on
                    // the name row. The state badges (`stateChips`) live in a SIBLING
                    // block outside that container — keeping them inside it grew the
                    // container to two lines when the "In trash" badge showed and
                    // dropped the dot off the name line.
                    const recordLabel = (
                        <RecordLabel
                            key={id}
                            link={getRelativeUrl(resource_type, id)}
                            name={name}
                            commentCount={comment_count}
                        />
                    );
                    // The name is the navigation link (RecordLabel). An editable row
                    // also gets the inline name-edit pencil at the TRAILING end of the
                    // cell — after the state-badge strip — so it sits at the right end
                    // of the icon set, never between the name and the badges. Placement
                    // + hover-reveal is defined once on `.nameCellWithBadges`
                    // (Badges.module.scss), shared with the other RecordTable name cells.
                    return (
                        <div className={badgeStyle.nameCellWithBadges}>
                            <div className={dotStyle.relativePositionedContainer}>
                                <ProvisioningStateDot id={id} />
                                {recordLabel}
                            </div>
                            <StateBadges>
                                <InTrashBadge deletedAt={deleted_at} />
                            </StateBadges>
                            {isRecordFieldEditable(row) && (
                                <RecordFieldInlineEditPencil
                                    record={row}
                                    field="name"
                                    value={name}
                                    resourceName="custom_object"
                                    refresh={refresh}
                                />
                            )}
                        </div>
                    );
                },
            },
            // Normal record fields (created/modified by & at, location) — reuse the
            // shared record-table columns so this list matches every other resource list.
            locationColumn(),
            commonColumns.modifiedAt(),
            commonColumns.modifiedBy(),
            commonColumns.createdAt(),
            commonColumns.createdBy(),
            {
                label: "In trash",
                key: "deleted_at",
                shown: false,
                sortable: true,
                dto: ({ deleted_at, id, resource_type }) => {
                    return (
                        <RecordLabel
                            key={id}
                            link={getRelativeUrl(resource_type, id)}
                            icon={deleted_at !== null ? IconCollection.check : undefined}
                        />
                    );
                },
            },
            // Addable (not shown by default, so the default column set is unchanged)
            // Description column with a pop-out markdown editor (scope (ii) slice 2).
            // `description` is a real top-level custom_object column, so it sorts
            // server-side like name.
            {
                label: "Description",
                key: "description",
                shown: false,
                sortable: true,
                cellStyle: getRecordColumnCellStyle("description"),
                dto: (row, refresh) => (
                    <RecordDescriptionInlineEdit
                        record={row}
                        value={row?.description}
                        resourceName="custom_object"
                        refresh={refresh}
                    />
                ),
            },
            // Attribute columns are sortable here: the object_template CO list is
            // server-side paged (updateQuery is wired), and the custom_object_list
            // GraphQL order arg accepts "+value.<attr>"/"-value.<attr>". Passing
            // sortable keys each attribute column as `value.<attr>` so a header
            // click emits the correct order token.
            ...getSchemaFormColumns(currentSchema, { sortable: true, editable: true }),
        ];
    }, [objectTemplateList, recordId]);

    const onAddClick = useCallback(() => {
        history.push(
            getNewRecordPath("custom_object", {
                object_template_id: recordId,
                object_template_name: resourceData?.name,
            })
        );
    }, [history, recordId, resourceData?.name]);

    // Jump to the advanced search prefiltered to this object template's custom objects.
    // object_template_id is a real custom_object column, so it works as a customFilter
    // independent of value.<attr> attribute-value filtering.
    const onOpenAdvancedSearch = useCallback(() => {
        history.push(
            buildRecordListAdvancedSearchPath("custom_object", {
                and: [{ field: "object_template_id", op: "eq", value: recordId }],
            })
        );
    }, [history, recordId]);

    // One-click trash visibility: flip only allow_deleted (show/hide trashed
    // rows). Normal (non-trashed) rows are always shown (allow_normal:true) — the
    // "hide non-trashed" granularity lives in the advanced search, not here. Reset
    // to the first page since the row set changes.
    const onTrashToggle = useCallback(
        (next: boolean) => {
            updateCustomObjectListQuery({
                allow_deleted: next,
                allow_normal: true,
                offset: 0,
            });
        },
        [updateCustomObjectListQuery]
    );

    // The trash control moves into the list-controls cluster (next to density /
    // columns) as a one-click toggle; only the "Show all in advanced search"
    // jump stays in the list header.
    const trashToggle = useMemo(
        () => <TableTrashToggle showTrash={!!showTrash} onToggle={onTrashToggle} />,
        [showTrash, onTrashToggle]
    );

    const headerAction = useMemo(
        () => (
            <ShowAllButton
                onClick={onOpenAdvancedSearch}
                title="Show all these custom objects in the advanced search"
            />
        ),
        [onOpenAdvancedSearch]
    );

    const tableOptions = useMemo(
        () => ({
            viewColumns: true,
        }),
        []
    );

    return (
        <SubrecordTable
            key={customObjectListKey}
            data={customObjectData as Custom_Object[]}
            totalItemCount={
                resourceData?.custom_object_list_object_template_id?.custom_object_count || 0
            }
            uniqueKey={customObjectListKey}
            title="Custom objects"
            refreshData={fetchResourceData}
            columns={columns}
            emptyListText="No data to display."
            initialQuery={getInitialQueryWithTableSettings(
                `customObjectList-${recordId}`,
                initialQuery
            )}
            updateQuery={updateCustomObjectListQuery}
            isAddButtonDisabled={!!resourceData?.deleted_at}
            onAddClick={onAddClick}
            headerAction={headerAction}
            customRightToolbarChildren={trashToggle}
            tableOptions={tableOptions}
            tableSettingsKey={`customObjectList-${recordId}`}
        />
    );
};
